import {classList, dataset, listen} from 'nuffjs';
import {BaseField} from './base-field';

/**
 * Configuration values
 *
 * @property {string} toggledClass=toggled if an internal {@link ContextualMenuToggler} is toggled,
 * this class is set on the field as well
 * @property {string} showAllClass=showAll class set when the showAll action is performed
 * @property {string} resetAttr=reset data-attribute to intercept reset actions
 * @property {string} setEmptyAttr=setEmpty data-attribute to intercept setEmpty actions
 * @property {string} showAllAttr=showAll data-attribute to intercept showAll actions
 * @property {string} toggleEvent=contextualMenuToggler:toggle events to listen to detect the
 * eventual interal ContextualMenuToggler toggling
 * @memberof OptionsField
 * @extends BaseField.conf
 */
const conf = Object.assign({}, BaseField.conf, {
	toggledClass: 'toggled',
	showAllClass: 'showAll',
	resetAttr: 'reset',
	setEmptyAttr: 'setEmpty',
	showAllAttr: 'showAll',
	toggleEvent: 'contextualMenuToggler:toggle',
});

/**
 * @classdesc Options Field: complex field used to represent facet properties in connection to the
 * {@link Search} component. It can be internally composed by a set of checkboxes (allowing multiple
 * values to be selected) or radio buttons (allowing only a single value to be selected) and can be
 * visually represented in several different ways: as a dropdown, as clickable tags, and so on. It
 * also support to show by default only a limited amount of items, to be expanded on user's request.
 *
 * @class OptionsField
 * @extends {BaseField}
 * @version 1.0.0
 * @path
 */
class OptionsField extends BaseField {
	static conf = conf;
	static defaultOpts = Object.assign({}, super.defaultOpts, {
		multiple: false,
		initialValue: [],
		allowRadioDeselect: true,
	});

	firstConnectedCallback() {
		super.firstConnectedCallback();
		this.initialValue = this.opts.initialValue;
		this.previousValue = this.readValue();
		listen(this, conf.eventPrefix + 'change', this.onChange.bind(this), {capture: true});
		listen(this, 'click', this.onResetClick.bind(this), {
			selector: dataset.selector(conf.resetAttr),
		});
		listen(this, 'click', this.onSetEmptyClick.bind(this), {
			selector: dataset.selector(conf.setEmptyAttr),
		});
		listen(this, 'click', this.onShowAllToggle.bind(this), {
			selector: dataset.selector(conf.showAllAttr),
		});
		listen(this, conf.toggleEvent, this.onTogglerChange.bind(this));

		if (this.opts.allowRadioDeselect) {
			listen(this, 'click', this.onRadioOptionClick.bind(this), {selector: 'input', capture: true});
		}
	}

	onChange(event) {
		this.previousValue = this.readValue();
	}

	onResetClick(event) {
		const oldValue = this.readValue();
		this.reset();
		this.previousValue = this.initialValue;

		if (
			oldValue.length !== this.initialValue.length ||
			oldValue.sort().join(',') !== this.initialValue.sort().join(',')
		) {
			this.triggerEvent('change');
		}
	}

	onSetEmptyClick(event) {
		this.setEmpty(true);
	}

	onTogglerChange(event) {
		classList(this).toggle(conf.toggledClass, event.detail.toggled);
	}

	onShowAllToggle(event) {
		classList(this).toggle(conf.showAllClass);
	}

	onRadioOptionClick(event, target) {
		if (this.previousValue.length && String(this.previousValue[0]) === String(target.value)) {
			this.setValue([]);
			this.triggerEvent('change');
			this.previousValue = this.readValue();
		}
	}

	getInput() {
		return [...this.querySelectorAll('input')];
	}

	writeValue(value) {
		if (!Array.isArray(value)) {
			value = [value];
		}
		for (const input of this.getInput()) {
			input.checked = value.includes(input.value);
		}
	}

	readValue() {
		const value = [];
		for (const input of this.getInput()) {
			if (input.checked) {
				value.push(input.value);
			}
		}
		return value;
	}

	setFocus() {
		this.getInput()[0].focus();
	}

	setBlur() {
		for (const input of this.getInput()) {
			input.blur();
		}
	}

	// toggleInputEnabled(value) {
	// 	for (const input of this.getInput()) {
	// 		input.disabled = !value;
	// 	}
	// }

	isMulti() {
		return this.opts.multiple;
	}

	setEmpty(triggerChangeEvent = true) {
		const oldValue = this.readValue();
		const value = [];
		this.setValue(value);
		this.previousValue = value;

		if (triggerChangeEvent && oldValue.length > 0) {
			this.triggerEvent('change');
		}
	}

	hasValue() {
		return this.getValue().length > 0;
	}
}

export {OptionsField};
